/*
 * Typing, as a conversation's screen shows it: who is typing there, from the live socket's typing
 * hints, each forgotten a few seconds after it was last said (so someone whose client vanished
 * stops showing), and the Member's own typing told to the others no more often than it needs.
 * Typing is never kept anywhere: it is what was said in the last few seconds.
 */

/** How long someone shows as typing after their client last said so. */
export const typingShownFor = 6_000

/** How often, at most, the Member's client says they are typing while they go on. */
export const typingSaidEvery = 3_000

/** Who is typing, by Member id, each with when they stop showing; in the order they started. */
export type TypingState = ReadonlyMap<string, number>

export const noTyping: TypingState = new Map()

/** The state after a typing hint arrived at `now`. */
export function withTyping(
  state: TypingState,
  hint: { memberId: string; typing: boolean },
  now: number,
): TypingState {
  const next = new Map(state)
  if (hint.typing) next.set(hint.memberId, now + typingShownFor)
  else next.delete(hint.memberId)
  return next
}

/** Who shows as typing at `now`, in the order they started. */
export function typistsAt(state: TypingState, now: number): string[] {
  return [...state].filter(([, until]) => until > now).map(([memberId]) => memberId)
}

/** When the next of them stops showing, after `now`; none when nobody shows. */
export function nextExpiry(state: TypingState, now: number): number | undefined {
  const ahead = [...state.values()].filter((until) => until > now)
  return ahead.length ? Math.min(...ahead) : undefined
}

/** The line under a conversation saying who is typing; empty when nobody is. */
export function describeTyping(names: readonly string[]): string {
  if (names.length === 0) return ""
  if (names.length === 1) return `${names[0]} is typing…`
  if (names.length > 3) return "Several people are typing…"
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)} are typing…`
}

/**
 * Tells the conversation's others the Member is typing: `typed` on each change to what they write
 * says so at most every `typingSaidEvery`, and `stopped` (sent, or emptied) says they stopped, once,
 * when it last said they were typing.
 */
export function typingSender({
  say,
  now = () => Date.now(),
}: {
  say(typing: boolean): void
  now?: () => number
}) {
  let saidAt: number | undefined
  return {
    typed() {
      const at = now()
      if (saidAt !== undefined && at - saidAt < typingSaidEvery) return
      saidAt = at
      say(true)
    },
    stopped() {
      if (saidAt === undefined) return
      saidAt = undefined
      say(false)
    },
  }
}
